const SUCCESS = 'urn:dx:apdServerPanel:success';
const PENDING = 'urn:dx:apdServerPanel:access-pending';
const NO_ACCESS = 'urn:dx:apdServerPanel:no-access';

function extractBody(source: unknown): unknown {
  if (source == null) return undefined;
  if (
    typeof source === 'object' &&
    source !== null &&
    'error' in source &&
    'status' in source &&
    typeof (source as { status?: unknown }).status === 'number'
  ) {
    return (source as { error?: unknown }).error;
  }
  return source;
}

function inferPendingFromText(combined: string): boolean {
  const s = combined.toLowerCase();
  return (
    s.includes('request sent') ||
    s.includes('request has been sent') ||
    s.includes('request has been submitted') ||
    s.includes('access request is pending') ||
    s.includes('awaiting approval')
  );
}

function inferDeniedFromText(combined: string): boolean {
  const s = combined.toLowerCase();
  return (
    s.includes('no access') ||
    s.includes('access denied') ||
    s.includes('does not have access') ||
    s.includes('not authorized') ||
    s.includes('not granted')
  );
}

/** Normalize ACL-APD `has_access` success body or error body to `{ type }`. */
export function normalizeAclAccessStatus(source: unknown): { type?: string } {
  let body = extractBody(source);

  if (typeof body === 'string') {
    const t = body.trim();
    if (!t) return {};
    try {
      body = JSON.parse(t);
    } catch {
      return inferPendingFromText(t) ? { type: PENDING } : {};
    }
  }

  if (body == null || typeof body !== 'object') {
    return {};
  }

  const o = body as Record<string, unknown>;
  const nested =
    typeof o['result'] === 'object' && o['result'] !== null
      ? (o['result'] as Record<string, unknown>)
      : typeof o['data'] === 'object' && o['data'] !== null
        ? (o['data'] as Record<string, unknown>)
        : undefined;

  const firstResult =
    Array.isArray(o['results']) && o['results'].length > 0 && typeof o['results'][0] === 'object'
      ? (o['results'][0] as Record<string, unknown>)
      : undefined;

  const typeRaw = [o['type'], o['accessType'], nested?.['type'], firstResult?.['type']].find(
    (x): x is string => typeof x === 'string',
  );
  const typeStr = typeRaw?.trim() ?? '';

  const combinedMsg = [o['detail'], o['title'], o['message'], nested?.['detail'], nested?.['title']]
    .filter((x): x is string => typeof x === 'string')
    .join(' ');

  if (typeStr) {
    const lower = typeStr.toLowerCase();
    if (lower === SUCCESS.toLowerCase() || lower.includes('apdserverpanel:success')) {
      return { type: SUCCESS };
    }
    if (lower.includes('access-pending') || lower.includes('apdserverpanel:access-pending')) {
      return { type: PENDING };
    }
    if (lower.includes('no-access') || lower.includes('apdserverpanel:no-access')) {
      return { type: NO_ACCESS };
    }
    if (lower.startsWith('urn:')) {
      return { type: typeStr };
    }
  }

  if (inferPendingFromText(combinedMsg)) {
    return { type: PENDING };
  }

  if (inferDeniedFromText(combinedMsg)) {
    return { type: NO_ACCESS };
  }

  return {};
}

/**
 * HTTP 200 from has_access is granted unless the body is explicitly pending or
 * no-access. Catalogue type URNs in the body must not be treated as ACL status.
 */
export function isGrantedHasAccessResponse(
  source: unknown,
  fromHttpOk: boolean,
): boolean {
  const t = (normalizeAclAccessStatus(source).type ?? '').trim();
  if (t === PENDING || t === NO_ACCESS) {
    return false;
  }
  if (fromHttpOk) {
    return true;
  }
  return t === SUCCESS;
}

export const ACL_ACCESS_SUCCESS = SUCCESS;
export const ACL_ACCESS_PENDING = PENDING;
export const ACL_ACCESS_NO_ACCESS = NO_ACCESS;
